import React, { useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getInitials, getRandomColor } from '../../utils/UserCustomDisplay';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import authNavigate from '../../globalComponents/AuthNav';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import FormThreadsTree from './FormThreadsTree';
import { useTimezone } from '../../utils/TimezoneContext';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import { openThreadDetails, loadEditingThread } from '../../../store/unstructuredThreadSlice';

const API_BASE_URL = '/pceapi/fabrics';
const THREADS_API_BASE_URL = '/pceapi/threads/unstructured';

const PROJECT_ROLE_LABELS = {
  PROJECT_MANAGER: 'Project Manager',
  DOSSIER_DEV_LEAD: 'Dossier Development Lead',
  DOSSIER_VIEWER: 'Dossier Viewer'
};

// Who runs the project reads first: Project Managers, then leads, then viewers.
const PROJECT_ROLE_ORDER = {
  PROJECT_MANAGER: 0,
  DOSSIER_DEV_LEAD: 1,
  DOSSIER_VIEWER: 2
};

const MEMBERS_PREVIEW_COUNT = 10;

const DataFabricDetails = ({ fabricDetails, onClose, onRefresh, onEditFabric, onEditThread }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const { formatDateTime } = useTimezone();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');
  const [showAllMembers, setShowAllMembers] = useState(false);
  const headers = { Authorization: GetCookie('access_token') };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting project "${fabric.name}"`);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete`,
        { headers, data: { fabric_id: fabric.id } }
      );
      const data = response.data;
      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to delete project');
        setIsErrorPopupVisible(true);
        return;
      }
      window.dispatchEvent(new Event('category_updated'));
      setPopupSuccessMessage(data.message || 'Project deleted successfully');
      setIsSuccessPopupVisible(true);
    } catch (error) {
      setPopupErrorMessage(error.message || 'Failed to delete project');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const handleViewThread = (threadId) => {
    dispatch(openThreadDetails());
    navigate('/data-manager', { state: { viewThreadId: threadId, fabricId: fabric.id } });
  };

  // Only the full record from /details may open the edit form. The tree row
  // it used to fall back on has no tags, so a form built from it saved an
  // empty list and wiped every meta tag on the folder.
  const handleEditThread = async (thread) => {
    let fullThread = null;
    try {
      const res = await axios.get(`${THREADS_API_BASE_URL}/details/${thread.id}`, { headers });
      const data = res.data || {};
      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || "Couldn't load this Data Folder for editing. Please try again.");
        setIsErrorPopupVisible(true);
        // Gone or unlinked since this page loaded: refresh the project's list.
        if (data.code === 'FOLDER_DELETED' && onRefresh) onRefresh();
        return;
      }
      fullThread = data.data?.thread || null;
    } catch {
      fullThread = null;
    }
    if (!fullThread) {
      setPopupErrorMessage("Couldn't load this Data Folder for editing. Please try again.");
      setIsErrorPopupVisible(true);
      return;
    }
    dispatch(loadEditingThread(fullThread));
    navigate('/data-manager', { state: { fabricId: fabric.id } });
  };

  const handleThreadSelection = (threadId) => {
    setSelectedThreadId(threadId === selectedThreadId ? null : threadId);
  };

  if (!fabricDetails) return null;

  const { fabric, project_members } = fabricDetails;

  // Per-project, not per-role: get_fabric_details answers can_edit / can_delete
  // with the same checks the update and delete endpoints run, so these are exact
  // for this project rather than "may this user ever edit something".
  const canEdit = Boolean(fabric.can_edit);
  const canDelete = Boolean(fabric.can_delete);

  const sortedMembers = [...(project_members || [])].sort(
    (a, b) => (PROJECT_ROLE_ORDER[a.role] ?? 99) - (PROJECT_ROLE_ORDER[b.role] ?? 99)
  );
  const hiddenMemberCount = Math.max(sortedMembers.length - MEMBERS_PREVIEW_COUNT, 0);
  const visibleMembers = showAllMembers || hiddenMemberCount === 0
    ? sortedMembers
    : sortedMembers.slice(0, MEMBERS_PREVIEW_COUNT);

  return (
    <div className="ev-card-4 ev-padding-xlarge2 w-100">
      {/* Top action buttons */}
      <div className="text-end d-flex justify-content-end gap-2">
        {canDelete && (
          <button
            className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
            onClick={() => setShowDeleteConfirm(true)}
            data-tooltip="Delete Project"
          >
            <i className="bi bi-trash"></i>
          </button>
        )}
        {canEdit && (
          <button
            className="ev-btn-skyblue-fill-round ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
            onClick={() => onEditFabric(fabric)}
            data-tooltip="Edit Project"
          >
            <i className="bi bi-pencil"></i>
          </button>
        )}
        {!canEdit && !canDelete && (
          <span className="ev-small-p-blue align-self-center me-2">
            <i className="bi bi-eye me-1"></i> View only
          </span>
        )}
        <button className="ev-btn-blue-outline-round ev-bold-reg" onClick={onClose}>
          <i className="bi bi-x-lg"></i> Close
        </button>
      </div>

      {/* Fabric header info */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
        <div className="col-5">
          <h4 className="ev-med-p-blue mb-2">
            {fabric.name} &nbsp;
            {/* <span className={`badge badge-sm ${fabric.fabric_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
              {fabric.fabric_access_level}
            </span> */}
          </h4>
          <p className="ev-small-p-dark mb-1">
            <span className="ev-message-card3 mb-2"><b>Owner.</b> {fabric.created_by_email}</span>
          </p>
          <p className="ev-small-p-dark mb-0">
            &nbsp; Created at  {formatDateTime(fabric.created_at)}
          </p>
          {fabric.updated_at && (
            <p className="ev-small-p-dark mb-0">
              &nbsp; Updated at {formatDateTime(fabric.updated_at)}
            </p>
          )}
        </div>

      </div>

      {/* Project members — these rows are what grant access to the project. */}
      <div className="ev-card-6 mb-4">
        <h5 className="ev-reg-p-blue mb-2">
          Project Members
          <span className="badge bg-warning ev-btn-small ms-2">
            {project_members ? project_members.length : 0}
          </span>
        </h5>
        {sortedMembers.length > 0 ? (
          <>
          <div className="row">
            {visibleMembers.map((member) => (
              <div key={member.user_id} className="col-6 mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="more-circle more-circle--default d-flex align-items-center justify-content-center text-white fw-bold me-3 avatar-tooltip"
                    style={{ backgroundColor: getRandomColor(member.email) }}
                    data-tooltip={member.email}
                  >
                    {getInitials(member.email)}
                  </div>
                  <span className="ev-small-p-dark text-truncate flex-grow-1">
                    {member.name || member.email}
                  </span>
                  <span className="ev-message-card3 ms-2">
                    {PROJECT_ROLE_LABELS[member.role] || member.role}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {hiddenMemberCount > 0 && (
            <div className="text-center mt-1">
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                onClick={() => setShowAllMembers(prev => !prev)}
              >
                {showAllMembers ? 'Show less' : `Show all ${sortedMembers.length} members`}
              </button>
            </div>
          )}
          </>
        ) : (
          <p className="ev-small-p-dark mb-0">No project members</p>
        )}
      </div>

      {fabric.description && (
        <div className="ev-card-6 mb-4">
          <h5 className="ev-reg-p-blue mb-1">Description</h5>
          <p className="ev-small-p-dark mb-0">{fabric.description}</p>
        </div>
      )}

      {/* Threads list with View + Edit per row */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="ev-card-2">
            <div className="card-body">
              <h5 className="ev-reg-p-blue mb-3">Data folders in this Project</h5>
              <FormThreadsTree
                onSelectThread={handleThreadSelection}
                selectedThreadId={selectedThreadId}
                fabricId={fabric.id}
                multiSelect={false}
                selection={false}
                apiBaseUrl={API_BASE_URL}
                onViewThread={handleViewThread}
                onEditThread={handleEditThread}
                showThreadActions={true}
                projectViewOnly={!canEdit && !canDelete}
              />
            </div>
          </div>
        </div>
      </div>

      {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
      {isErrorPopupVisible && (
        <ErrorPopup onClose={() => setIsErrorPopupVisible(false)} message={popupErrorMessage} />
      )}
      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={() => { setIsSuccessPopupVisible(false); onClose(); }}
          message={popupSuccessMessage}
        />
      )}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        objectName="project"
        itemName={fabric.name}
        bodyContent={
          <div>
            <p className="mb-0">This will permanently delete:</p>
            <ul>
              <li>The project metadata and settings</li>
              <li>All Data folders links within this project</li>
              <li>All feature suites and operational components within this project</li>
            </ul>
            <p className="mb-0">Note: The underlying Data folders and their documents will not be deleted.</p>
            <p className="text-danger mb-0">This action cannot be undone.</p>
          </div>
        }
      />
    </div>
  );
};

export default DataFabricDetails;